/**
 * Crew rotation panel (Feature 2): which crew works the day shift and which
 * works the night shift on each date. Where crews share hours (A and C on
 * days, B and D on nights), the date is the only thing that tells them apart.
 *
 * The 14-day preview is the check against a mis-entered start date, so it
 * computes crews with the shared contract's own arithmetic, never a copy:
 * every runtime value here comes from `@guild/shared/shift-rotation.js`, which
 * has zero imports. The `@guild/shared` barrel would pull zod into the web.
 *
 * "Today" is the org-local date, `localDayNumber(new Date(), orgTimezone)`.
 * Never `toISOString()`: that is the UTC date, a day ahead from 19:00 CDT,
 * which would shift every row by one. Never the browser's zone either: until
 * `orgTimezone` arrives from /api/me the preview waits, and a zone Intl does
 * not recognise (NaN) shows a message instead of rows.
 *
 * Managers read; admins set up, edit and remove. The server enforces the same
 * split, so `isAdmin` only hides controls. The panel renders whatever the
 * server stored, even a rotation that fails `rotationProblems`, so that
 * Remove rotation stays reachable when the stored value is bad.
 */

import { useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { AlertCircle, CalendarClock, Loader2, Pencil, Trash2 } from 'lucide-react';
import {
  dayNumberToYmd,
  localDayNumber,
  rotationCrewsOnDay,
  rotationProblems,
  type ShiftRotation,
} from '@guild/shared/shift-rotation.js';
import {
  useDeleteShiftRotation,
  useSaveShiftRotation,
  useShiftRotation,
  useShifts,
} from '../../../hooks/useShifts';
import { useCurrentUser } from '../../../hooks/useUsers';
import { ApiError } from '../../../lib/api';

const PREVIEW_DAYS = 14;

const INPUT_CLASS =
  'w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm transition-all duration-200 placeholder:text-gray-400 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20 disabled:bg-gray-50 disabled:text-gray-500';
const LABEL_CLASS = 'mb-2 block text-sm font-semibold text-gray-700';
const HELP_CLASS = 'mt-1.5 text-xs text-gray-500';
const PRIMARY_BUTTON_CLASS =
  'inline-flex items-center gap-2 rounded-xl bg-guild-green px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-guild-green/20 transition-all duration-200 hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50';
const SECONDARY_BUTTON_CLASS =
  'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * 6 -> "06:00". Local on purpose: ShiftsPage's 12-hour `formatHour` can't be
 * imported, because ShiftsPage imports this panel.
 */
function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/** A start-hour field as a number. '' is NaN, never 0, so an emptied field is a problem, not midnight. */
function hourFromInput(value: string): number {
  return value.trim() === '' ? NaN : Number(value);
}

/** "a, c" -> ['a', 'c']: split on commas or whitespace, trim, lower-case, drop empties. */
function parseCrewList(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((code) => code.trim().toLowerCase())
    .filter((code) => code !== '');
}

interface FormValues {
  anchorDate: string;
  dayStart: string;
  nightStart: string;
  dayCrews: string;
  nightCrews: string;
}

/**
 * The form's starting text: a saved rotation's values, or for a new rotation
 * an EMPTY start date (required, with no default: EM review F7) and 6 and 18.
 */
function formValues(rotation: ShiftRotation | null): FormValues {
  if (rotation === null) {
    return { anchorDate: '', dayStart: '6', nightStart: '18', dayCrews: '', nightCrews: '' };
  }
  return {
    anchorDate: rotation.anchorDate,
    dayStart: String(rotation.dayStartHour),
    nightStart: String(rotation.nightStartHour),
    dayCrews: rotation.dayCrews.join(', '),
    nightCrews: rotation.nightCrews.join(', '),
  };
}

/**
 * What to show for a failed save or remove. A Zod 400 puts the rule messages
 * in `details.errors` under a generic "Invalid request data"; other API errors
 * carry theirs in `message`.
 */
function errorMessages(err: unknown, fallback: string): string[] {
  if (err instanceof ApiError) {
    const issues = (err.details as { errors?: { message?: string }[] } | undefined)?.errors;
    const messages = (issues ?? [])
      .map((issue) => issue.message)
      .filter((message): message is string => !!message);
    return messages.length > 0 ? messages : [err.message];
  }
  return [err instanceof Error ? err.message : fallback];
}

interface PreviewRow {
  date: string;
  dayCrew: string;
  nightCrew: string;
}

/**
 * The crews on `today` and the 13 dates after it, or null if any date can't be
 * evaluated: `rotationCrewsOnDay` returns null for a rotation it can't read and
 * for a day that is not an integer, such as a NaN "today". The caller shows a
 * message for null, never blank rows.
 */
function previewRows(rotation: ShiftRotation, today: number): PreviewRow[] | null {
  const rows: PreviewRow[] = [];
  for (let offset = 0; offset < PREVIEW_DAYS; offset++) {
    const crews = rotationCrewsOnDay(rotation, today + offset);
    if (crews === null) return null;
    rows.push({
      date: dayNumberToYmd(today + offset),
      dayCrew: crews.dayCrew,
      nightCrew: crews.nightCrew,
    });
  }
  return rows;
}

/** "Name (code)", or the bare code when no active shift has it. */
function crewLabel(code: string, nameByCode: ReadonlyMap<string, string>): string {
  const name = nameByCode.get(code.toLowerCase());
  return name === undefined ? code : `${name} (${code})`;
}

// ============================================
// PANEL
// ============================================

interface CrewRotationPanelProps {
  isAdmin: boolean;
}

export function CrewRotationPanel({ isAdmin }: CrewRotationPanelProps) {
  const rotationQuery = useShiftRotation();
  // Active shifts only: crew codes must name active shifts.
  const shiftsQuery = useShifts({ includeInactive: false });
  const { data: currentUser } = useCurrentUser();
  const orgTimezone = currentUser?.orgTimezone;
  const [editing, setEditing] = useState(false);

  const activeShifts = shiftsQuery.data?.filter((shift) => shift.isActive);
  // null until the shifts load: checked against an empty list instead, every
  // code would read as unknown.
  const activeCodes = activeShifts?.map((shift) => shift.code) ?? null;
  // null while the shifts load, so the preview waits instead of flashing bare
  // codes; empty if they failed, so it shows bare codes.
  const nameByCode: ReadonlyMap<string, string> | null =
    activeShifts !== undefined
      ? new Map(activeShifts.map((shift) => [shift.code.toLowerCase(), shift.name]))
      : shiftsQuery.isError
        ? new Map()
        : null;

  const rotation = rotationQuery.data;
  // A stored rotation that fails the shape rules can't be summarised, previewed
  // or used to pre-fill the form; Edit then starts from a blank form.
  const readableRotation =
    rotation !== undefined && rotation !== null && rotationProblems(rotation).length === 0
      ? rotation
      : null;

  let body: ReactNode;
  if (rotationQuery.isPending) {
    body = (
      <p className="flex items-center gap-2 text-sm text-gray-500">
        <Loader2 size={16} className="text-guild-green animate-spin" aria-hidden="true" />
        Loading crew rotation...
      </p>
    );
  } else if (rotationQuery.isError || rotation === undefined) {
    body = (
      <p className="flex items-center gap-2 text-sm text-red-700">
        <AlertCircle size={16} className="flex-shrink-0 text-red-500" aria-hidden="true" />
        Couldn&apos;t load the crew rotation. Reload the page to try again.
      </p>
    );
  } else if (isAdmin && editing) {
    body = (
      <RotationForm
        initial={readableRotation}
        activeCodes={activeCodes}
        shiftsFailed={shiftsQuery.isError}
        nameByCode={nameByCode}
        orgTimezone={orgTimezone}
        onDone={() => setEditing(false)}
      />
    );
  } else if (rotation === null) {
    body = (
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          No crew rotation. KPI results are attributed by each shift&apos;s hours.
        </p>
        {isAdmin && (
          <button type="button" onClick={() => setEditing(true)} className={PRIMARY_BUTTON_CLASS}>
            Set up crew rotation
          </button>
        )}
      </div>
    );
  } else {
    body = (
      <SavedRotation
        rotation={rotation}
        readable={readableRotation !== null}
        isAdmin={isAdmin}
        activeCodes={activeCodes}
        nameByCode={nameByCode}
        orgTimezone={orgTimezone}
        onEdit={() => setEditing(true)}
      />
    );
  }

  return (
    <section
      aria-labelledby="crew-rotation-title"
      className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
    >
      <div className="flex items-center gap-3 border-b border-gray-100 px-6 py-5">
        <div className="bg-guild-mint/50 flex h-10 w-10 items-center justify-center rounded-xl">
          <CalendarClock size={20} className="text-guild-green" aria-hidden="true" />
        </div>
        <div>
          <h2 id="crew-rotation-title" className="text-lg font-bold text-gray-900">
            Crew rotation
          </h2>
          <p className="text-sm text-gray-500">
            For crews that share hours: which crew works the day and night shifts on each date.
          </p>
        </div>
      </div>
      <div className="p-6">{body}</div>
    </section>
  );
}

// ============================================
// SAVED ROTATION (read-only summary; admins also Edit and Remove)
// ============================================

interface SavedRotationProps {
  rotation: ShiftRotation;
  readable: boolean;
  isAdmin: boolean;
  activeCodes: string[] | null;
  nameByCode: ReadonlyMap<string, string> | null;
  orgTimezone: string | undefined;
  onEdit: () => void;
}

function SavedRotation({
  rotation,
  readable,
  isAdmin,
  activeCodes,
  nameByCode,
  orgTimezone,
  onEdit,
}: SavedRotationProps) {
  const deleteMutation = useDeleteShiftRotation();
  const [removeErrors, setRemoveErrors] = useState<string[] | null>(null);

  // A saved rotation that names a since-deactivated shift shows its problem
  // here, on load: the only sign that its crew's results go unattributed.
  const problems =
    activeCodes === null ? rotationProblems(rotation) : rotationProblems(rotation, activeCodes);

  const handleRemove = async () => {
    if (
      !window.confirm(
        "Remove the crew rotation? New KPI results will be attributed by each shift's hours. Results already saved keep their shift."
      )
    ) {
      return;
    }
    setRemoveErrors(null);
    try {
      await deleteMutation.mutateAsync();
    } catch (err) {
      setRemoveErrors(errorMessages(err, 'The crew rotation could not be removed.'));
    }
  };

  const cycleDays = readable ? rotation.dayCrews.length : 0;

  return (
    <div className="space-y-6">
      {readable ? (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
          <SummaryItem label="Start date">
            <span className="font-mono">{rotation.anchorDate}</span>
          </SummaryItem>
          <SummaryItem label="Day shift">
            {`${formatHour(rotation.dayStartHour)}–${formatHour(rotation.nightStartHour)}`}
          </SummaryItem>
          <SummaryItem label="Night shift">
            {`${formatHour(rotation.nightStartHour)}–${formatHour(rotation.dayStartHour)}`}
          </SummaryItem>
          <SummaryItem label="Cycle">{`${cycleDays} ${cycleDays === 1 ? 'day' : 'days'}`}</SummaryItem>
          <SummaryItem label="Day crews" wide>
            <span className="font-mono">{rotation.dayCrews.join(', ')}</span>
          </SummaryItem>
          <SummaryItem label="Night crews" wide>
            <span className="font-mono">{rotation.nightCrews.join(', ')}</span>
          </SummaryItem>
        </dl>
      ) : (
        <p className="text-sm text-gray-600">The saved crew rotation can&apos;t be read.</p>
      )}

      {problems.length > 0 && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
          <p className="mb-2 text-sm font-semibold text-red-800">
            Until these are fixed, KPI results the rotation can&apos;t resolve are saved without a
            shift.
          </p>
          <ProblemList problems={problems} />
        </div>
      )}

      {readable && (
        <RotationPreview rotation={rotation} orgTimezone={orgTimezone} nameByCode={nameByCode} />
      )}

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onEdit}
            disabled={deleteMutation.isPending}
            className={SECONDARY_BUTTON_CLASS}
          >
            <Pencil size={14} aria-hidden="true" />
            Edit
          </button>
          <button
            type="button"
            onClick={() => void handleRemove()}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {deleteMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 size={14} aria-hidden="true" />
            )}
            Remove rotation
          </button>
        </div>
      )}
      {removeErrors !== null && <ErrorBox messages={removeErrors} />}
    </div>
  );
}

function SummaryItem({
  label,
  wide = false,
  children,
}: {
  label: string;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={wide ? 'col-span-2 sm:col-span-4' : undefined}>
      <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );
}

// ============================================
// FORM (admins)
// ============================================

interface RotationFormProps {
  /** The saved rotation to edit, or null for a new one (or one too broken to pre-fill). */
  initial: ShiftRotation | null;
  activeCodes: string[] | null;
  shiftsFailed: boolean;
  nameByCode: ReadonlyMap<string, string> | null;
  orgTimezone: string | undefined;
  onDone: () => void;
}

function RotationForm({
  initial,
  activeCodes,
  shiftsFailed,
  nameByCode,
  orgTimezone,
  onDone,
}: RotationFormProps) {
  const [values, setValues] = useState<FormValues>(() => formValues(initial));
  const [apiErrors, setApiErrors] = useState<string[] | null>(null);
  const saveMutation = useSaveShiftRotation();
  const isPending = saveMutation.isPending;

  const draft: ShiftRotation = {
    anchorDate: values.anchorDate,
    dayStartHour: hourFromInput(values.dayStart),
    nightStartHour: hourFromInput(values.nightStart),
    dayCrews: parseCrewList(values.dayCrews),
    nightCrews: parseCrewList(values.nightCrews),
  };
  // Shape only (no shift codes): what the preview needs.
  const shapeProblems = rotationProblems(draft);
  // Everything, once the active shifts have loaded. An empty start date fails
  // here with "Start date must be YYYY-MM-DD", which keeps Save disabled.
  const problems = activeCodes === null ? shapeProblems : rotationProblems(draft, activeCodes);
  const canSave = activeCodes !== null && problems.length === 0 && !isPending;

  const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setValues((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    setApiErrors(null);
    try {
      await saveMutation.mutateAsync(draft);
      onDone();
    } catch (err) {
      setApiErrors(errorMessages(err, 'The crew rotation could not be saved.'));
    }
  };

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-6">
      <div>
        <label htmlFor="crew-rotation-start" className={LABEL_CLASS}>
          Start date{' '}
          <span className="text-red-500" aria-hidden="true">
            *
          </span>
        </label>
        <input
          id="crew-rotation-start"
          type="date"
          required
          value={values.anchorDate}
          onChange={update('anchorDate')}
          aria-describedby="crew-rotation-start-help"
          disabled={isPending}
          className={`${INPUT_CLASS} sm:max-w-xs`}
        />
        <p id="crew-rotation-start-help" className={HELP_CLASS}>
          Pick a date on the posted crew schedule. The first crew in each list works this
          date&apos;s day shift and the night shift that starts this evening.
        </p>
      </div>

      <div>
        <div className="grid grid-cols-2 gap-4 sm:max-w-md">
          <div>
            <label htmlFor="crew-rotation-day-start" className={LABEL_CLASS}>
              Day shift starts
            </label>
            <input
              id="crew-rotation-day-start"
              type="number"
              min={0}
              max={23}
              step={1}
              value={values.dayStart}
              onChange={update('dayStart')}
              aria-describedby="crew-rotation-hours-help"
              disabled={isPending}
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label htmlFor="crew-rotation-night-start" className={LABEL_CLASS}>
              Night shift starts
            </label>
            <input
              id="crew-rotation-night-start"
              type="number"
              min={0}
              max={23}
              step={1}
              value={values.nightStart}
              onChange={update('nightStart')}
              aria-describedby="crew-rotation-hours-help"
              disabled={isPending}
              className={INPUT_CLASS}
            />
          </div>
        </div>
        <p id="crew-rotation-hours-help" className={HELP_CLASS}>
          Whole hours from 0 to 23, in the organization&apos;s time zone.
        </p>
      </div>

      <div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="crew-rotation-day-crews" className={LABEL_CLASS}>
              Day crews
            </label>
            <input
              id="crew-rotation-day-crews"
              type="text"
              value={values.dayCrews}
              onChange={update('dayCrews')}
              placeholder="e.g. a, c"
              autoComplete="off"
              spellCheck={false}
              aria-describedby="crew-rotation-crews-help"
              disabled={isPending}
              className={`${INPUT_CLASS} font-mono`}
            />
          </div>
          <div>
            <label htmlFor="crew-rotation-night-crews" className={LABEL_CLASS}>
              Night crews
            </label>
            <input
              id="crew-rotation-night-crews"
              type="text"
              value={values.nightCrews}
              onChange={update('nightCrews')}
              placeholder="e.g. b, d"
              autoComplete="off"
              spellCheck={false}
              aria-describedby="crew-rotation-crews-help"
              disabled={isPending}
              className={`${INPUT_CLASS} font-mono`}
            />
          </div>
        </div>
        <p id="crew-rotation-crews-help" className={HELP_CLASS}>
          Shift codes, one per day of the cycle, starting with the start date. Separate them with
          commas or spaces.
        </p>
      </div>

      <ProblemList problems={problems} />
      {activeCodes === null && (
        <p className="text-sm text-gray-500">
          {shiftsFailed
            ? "Couldn't load your shifts, so the crew codes can't be checked. Reload the page to try again."
            : 'Loading your shifts to check the crew codes...'}
        </p>
      )}

      <RotationPreview
        rotation={shapeProblems.length === 0 ? draft : null}
        orgTimezone={orgTimezone}
        nameByCode={nameByCode}
      />

      {apiErrors !== null && <ErrorBox messages={apiErrors} />}

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onDone}
          disabled={isPending}
          className={SECONDARY_BUTTON_CLASS}
        >
          Cancel
        </button>
        <button type="submit" disabled={!canSave} className={PRIMARY_BUTTON_CLASS}>
          {isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          Save
        </button>
      </div>
    </form>
  );
}

// ============================================
// 14-DAY PREVIEW
// ============================================

interface RotationPreviewProps {
  /** A rotation with no shape problems, or null for a draft that isn't valid yet. */
  rotation: ShiftRotation | null;
  orgTimezone: string | undefined;
  nameByCode: ReadonlyMap<string, string> | null;
}

function RotationPreview({ rotation, orgTimezone, nameByCode }: RotationPreviewProps) {
  let content: ReactNode;
  // Named only beside real rows: next to a message it would read as a working preview.
  let zoneLabel: string | null = null;
  if (rotation === null) {
    content = (
      <PreviewMessage>
        The preview appears once the start date, both start hours and both crew lists are valid.
      </PreviewMessage>
    );
  } else if (orgTimezone === undefined || nameByCode === null) {
    content = (
      <PreviewMessage>
        <Loader2 size={14} className="text-guild-green animate-spin" aria-hidden="true" />
        Loading the preview...
      </PreviewMessage>
    );
  } else {
    // Org-local today: never the UTC date, never the browser's zone.
    const today = localDayNumber(new Date(), orgTimezone);
    const rows = previewRows(rotation, today);
    if (rows === null) {
      content = (
        <PreviewMessage>
          {Number.isNaN(today)
            ? `Can't show the preview: the organization's time zone (${orgTimezone}) isn't recognized.`
            : "Can't show a preview for this rotation."}
        </PreviewMessage>
      );
    } else {
      zoneLabel = orgTimezone;
      content = (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-sm" aria-labelledby="crew-rotation-preview-title">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500">
                  Date
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500">
                  {`Day: ${formatHour(rotation.dayStartHour)}–${formatHour(rotation.nightStartHour)}`}
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500">
                  {`Night: starts ${formatHour(rotation.nightStartHour)} on this date, ends ${formatHour(rotation.dayStartHour)} the next day`}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.date}>
                  <th
                    scope="row"
                    className="whitespace-nowrap px-4 py-2.5 text-left font-mono font-normal text-gray-700"
                  >
                    {row.date}
                  </th>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-900">
                    {crewLabel(row.dayCrew, nameByCode)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-900">
                    {crewLabel(row.nightCrew, nameByCode)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
  }

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
        <h3 id="crew-rotation-preview-title" className="text-sm font-semibold text-gray-700">
          Next {PREVIEW_DAYS} days
        </h3>
        {zoneLabel !== null && (
          <span className="text-xs text-gray-500">from today in {zoneLabel}</span>
        )}
      </div>
      {content}
    </div>
  );
}

function PreviewMessage({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 px-4 py-3 text-sm text-gray-500">
      {children}
    </p>
  );
}

// ============================================
// MESSAGES
// ============================================

/** Live problems, read out as they change. Always rendered, so the live region exists before its first message. */
function ProblemList({ problems }: { problems: string[] }) {
  return (
    <ul aria-live="polite" className="space-y-1">
      {problems.map((problem, index) => (
        <li key={index} className="flex items-start gap-2 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0 text-red-500" aria-hidden="true" />
          {problem}
        </li>
      ))}
    </ul>
  );
}

function ErrorBox({ messages }: { messages: string[] }) {
  return (
    <div role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
      <ul className="space-y-1">
        {messages.map((message, index) => (
          <li key={index} className="text-sm font-medium text-red-700">
            {message}
          </li>
        ))}
      </ul>
    </div>
  );
}
